import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from "../lib/supabase";
import { LightBulbIcon } from '@heroicons/react/24/solid';

export default function EnhancedProfile() {
  const navigate = useNavigate();
  const [about, setAbout] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const MAX_CHARS = 2000;
  const MAX_DISPLAY_NAME_CHARS = 100;

  const env = (window as any).__ENV__;
  const supabase = getSupabaseClient();

  const handleAboutChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) {
      setAbout(text);
    }
  };

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_DISPLAY_NAME_CHARS) {
      setDisplayName(text);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setPhoto(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!about.trim()) {
      alert('Please write something about yourself.');
      return;
    }

    setLoading(true);
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        alert('User not logged in');
        return;
      }

      // If photo is provided, convert to base64
      if (photo) {
        const reader = new FileReader();
        reader.readAsDataURL(photo);
        reader.onloadend = async () => {
          const base64Data = reader.result as string;

          const res = await fetch(`${env.SUPABASE_URL}/functions/v1/enhanced-profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              about,
              display_name: displayName.trim() || null,
              image: base64Data,
              mimeType: photo.type,
            }),
          });

          const result = await res.json();

          if (!res.ok) {
            alert('Failed to save profile');
            return;
          }

          navigate('/onboarding/complete-profile');
        };
      } else {
        // No photo provided, just save the about text
        const res = await fetch(`${env.SUPABASE_URL}/functions/v1/enhanced-profile`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            about,
            display_name: displayName.trim() || null,
            image: null,
            mimeType: null,
          }),
        });

        const result = await res.json();

        if (!res.ok) {
          alert('Failed to save profile');
          return;
        }

        navigate('/onboarding/complete-profile');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-4 py-10">
      <div className="mx-auto max-w-3xl text-[#304031]">
        <h1 className="mb-3 text-center font-recoleta text-[42px] leading-[44px] font-medium text-[#304031]">
          Your Seeker-Facing Profile
        </h1>
        <p className="mb-12 text-center text-lg text-[#304031]/70">
          Upload a profile photo and write a description that helps seekers understand who you are and how you work. This is what potential clients will see when they're matched with you.
        </p>

        <div className="rounded-2xl border-2 border-[#a0978a] bg-[#fffcef] p-8 shadow-sm mb-8">
          {/* Display Name */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-[#304031] mb-3">
              Display Name <span className="text-[#304031]/50 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={handleDisplayNameChange}
              placeholder="Enter a name to show seekers (or leave blank to use your full name)"
              className="w-full rounded-lg border-2 border-[#a0978a] bg-[#fffcef] px-4 py-3 text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
            />
            <div className="mt-2 flex justify-between items-center">
              <p className="text-xs text-[#304031]/60">
                This name will be shown to seekers instead of your legal name. Useful for privacy or if you go by a different name professionally.
              </p>
              <p className={`text-xs ${displayName.length >= MAX_DISPLAY_NAME_CHARS ? 'text-red-500' : 'text-[#304031]/60'}`}>
                {displayName.length}/{MAX_DISPLAY_NAME_CHARS}
              </p>
            </div>
          </div>

          {/* Profile Photo Upload */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-[#304031] mb-3">
              Profile Photo <span className="text-[#304031]/50 font-normal">(optional)</span>
            </label>
            <div className="flex items-center gap-6">
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  className="h-24 w-24 rounded-full object-cover border-2 border-[#a0978a]"
                />
              ) : (
                <div className="h-24 w-24 flex items-center justify-center border-2 border-[#a0978a] rounded-full bg-[#304031]/5 text-[#304031]/50 text-sm">
                  No Photo
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="block w-full text-sm text-[#304031] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-2 file:border-[#304031] file:text-sm file:font-semibold file:bg-[#fffcef] file:text-[#304031] hover:file:bg-[#304031] hover:file:text-[#fef6d6] file:transition"
                />
                <p className="mt-2 text-xs text-[#304031]/60">
                  JPG, PNG or GIF. Max file size 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* About Text */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#304031] mb-3">
              About You <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={6}
              placeholder="Write a description about yourself and your approach..."
              value={about}
              onChange={handleAboutChange}
              className="w-full rounded-lg border-2 border-[#a0978a] bg-[#fffcef] px-4 py-3 text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
            />
            <div className="mt-2 flex justify-between items-center">
              <p className="text-xs text-[#304031]/60">
                Share your experience, approach, and what makes you unique as a guide. This helps seekers feel confident in connecting with you.
              </p>
              <p className={`text-xs ${about.length >= MAX_CHARS ? 'text-red-500' : 'text-[#304031]/60'}`}>
                {about.length}/{MAX_CHARS}
              </p>
            </div>
          </div>

          {/* Tips box */}
          <div className="p-4 border-2 border-[#a0978a] rounded-2xl bg-[#304031]/5">
            <div className="flex items-center gap-2 mb-3">
              <LightBulbIcon className="h-5 w-5 text-[#304031]" />
              <span className="text-sm font-semibold text-[#304031]">Tips for a Strong Introduction</span>
            </div>
            <ul className="space-y-2 text-sm text-[#304031]/80 ml-6">
              <li className="list-disc">Share a little about your background and what brought you to this work.</li>
              <li className="list-disc">Describe your general approach or style (gentle, structured, intuitive, trauma-informed, etc.).</li>
              <li className="list-disc">Mention any specific populations or themes you feel especially called to support.</li>
              <li className="list-disc">Keep the tone warm, grounded, and human. Seekers are often nervous when they reach out — your words can help put them at ease.</li>
            </ul>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-4 items-center justify-between">
          <button
            onClick={() => navigate('/onboarding/complete-profile')}
            disabled={loading}
            className="inline-flex items-center gap-2 border-2 border-[#304031] px-6 py-3 rounded-full hover:bg-[#304031]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-[#304031]"
          >
            ← Back
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !about.trim()}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition ${
              loading || !about.trim()
                ? 'bg-[#304031]/20 text-[#304031]/40 cursor-not-allowed'
                : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-[#fef6d6] border-t-transparent rounded-full"></div>
                Saving...
              </>
            ) : (
              'Save & Continue →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
